/* eslint
    @typescript-eslint/no-shadow: "warn"
*/

import Splide from '@splidejs/splide';
import PrescriptionModal, {
  ModalType
} from '@webModules/prescriptionModal';
import { element, globalEvent } from '@shared/easyfy.core';
import { invoke } from '@shared/easyfy.decorators';
import { IEasyfyEvent } from '@shared/easyfy.events/easyfy.eventdata';
import { MedsEventNames } from '@webModules/eventMappers/dataMapper.ga4';
import Events, {
  ServiceNames
} from '@shared/easyfy.events/easyfy.eventhandler';
import DialogModule from '@webModules/dialog.module';
import reviewService from '@webServices/review.service';
import { hasClass, replaceHtml } from '@shared/easyfy.utils';
import { defaultTranslations } from '@webModules/splideHelper';

namespace Easyfy.Web.Pages {
  @invoke
  class Product {
    static Instance: Product;

    private modal: PrescriptionModal;

    // Keep in sync with ProductReviewConstants.ReviewListPageSize (Meds.Core)
    private static reviewListPageSize = 20;

    constructor() {
      Product.Instance = this;
      this.modal = new PrescriptionModal();
    }

    onInit() {
      Product.mountImageSlider();
      Product.mountProductPackageSlider();
      Product.setReferredListName();

      globalEvent({
        type: 'click',
        event: {
          action: 'get-fass-modal',
          callback: Product.getFassModal
        }
      });

      globalEvent({
        type: 'click',
        event: {
          action: 'earlier-price-click',
          callback: Product.showPriceInformation
        }
      });

      globalEvent({
        type: 'click',
        event: {
          action: 'product-variant-click',
          callback: Product.productVariantClick
        }
      });

      globalEvent({
        type: 'click',
        event: {
          action: 'show-more-reviews',
          callback: Product.onShowMoreReviewsClick
        }
      });

      globalEvent({
        type: 'click',
        event: {
          action: 'filter-reviews-on-rating',
          callback: Product.onFilterReviewsOnRatingClick
        }
      });

      globalEvent({
        type: 'click',
        event: {
          action: 'reset-filters',
          callback: Product.onResetFiltersClick
        }
      });

      globalEvent({
        type: 'click',
        event: {
          action: 'go-to-review-list',
          callback: Product.onGoToReviewListClick
        }
      });

      const reviewDetails = element('[data-action="toggle-review-details"]');
      if (reviewDetails) {
        reviewDetails.addEventListener(
          'toggle',
          Product.onReviewDetailsToggle
        );
      }

      if (window.location.hash === '#product-review-details') {
        // When URL ends with this hash open review section and load reviews
        Product.onGoToReviewListClick();
      }
    }

    private static async onResetFiltersClick(e) {
      const selectedFilter = e.target.closest('.rating-filters').querySelector('.selected-filter');

      if (!selectedFilter) {
        return;
      }

      await Product.loadReviews(1);
    }

    private static async loadReviews(pageNumber: number, displayLoader: boolean = false) {
      const reviewDetailsElement = element('details[data-action="toggle-review-details"]');

      const detailsContentElement = reviewDetailsElement.querySelector('.details-content');

      if (displayLoader) {
        const loaderString = '<div class="loader loader-mini" role="status" aria-live="polite" aria-label="Laddar"></div>';

        detailsContentElement.innerHTML = loaderString;
      }

      const productId = reviewDetailsElement.getAttribute('data-productid');

      const apiResponse = await reviewService.GetProductReviews({
        productId,
        page: pageNumber,
        pageSize: Product.reviewListPageSize,
        onlyWithComments: true
      });

      if (apiResponse.status !== 200 || !apiResponse.data.success) {
        const errorTag = document.createElement('i');
        errorTag.classList.add('error-message');

        const errorMessage = apiResponse.data.message.value || "Något gick fel.";
        errorTag.textContent = errorMessage;

        replaceHtml(detailsContentElement, errorTag.outerHTML);

        return;
      }

      replaceHtml(reviewDetailsElement, apiResponse.data.partial);
    }

    static async onFilterReviewsOnRatingClick(e) {
      e.preventDefault();

      const pageNumber = 1; // reset page number when filtering on ratings

      let filterElement = e.target;

      if (!hasClass(filterElement, 'rating-filter')) {
        filterElement = filterElement.closest('.rating-filter');
      }

      // if the filter is already selected, we should clear it instead
      if (hasClass(filterElement, 'selected-filter')) {
        await Product.loadReviews(1);
        return;
      }

      const reviewDetailsElement = element('details[data-action="toggle-review-details"]');

      const productId = reviewDetailsElement.getAttribute('data-productid');

      const selectedRating = filterElement.getAttribute('data-filter-value');

      const apiResponse = await reviewService.GetProductReviews({
        productId,
        page: pageNumber,
        pageSize: Product.reviewListPageSize,
        ratings: [selectedRating],
        onlyWithComments: true
      });

      replaceHtml(reviewDetailsElement, apiResponse.data.partial);

      // Mark filter as selected after the list has been reloaded
      const selectedFilter = document.querySelector(`.rating-filter[data-filter-value="${selectedRating}"]`);

      selectedFilter.classList.add('selected-filter');
    }

    static async onShowMoreReviewsClick(e) {
      let showMoreReviewsButton = e.target;

      if (!hasClass(showMoreReviewsButton, 'show-more-reviews')) {
        showMoreReviewsButton = showMoreReviewsButton.closest('button.show-more-reviews');
      }

      if (!showMoreReviewsButton || !showMoreReviewsButton.getAttribute('data-page')) {
        return;
      }

      const currentPageNumber = parseInt(showMoreReviewsButton.getAttribute('data-page'), 10);

      await Product.loadReviews(currentPageNumber + 1);
    }

    static async onReviewDetailsToggle(e) {
      const reviewDetailsElement = element('details[data-action="toggle-review-details"]');
      const detailsContentElement = reviewDetailsElement.querySelector('.details-content');

      if (detailsContentElement && detailsContentElement.children.length > 0) {
        // Reviews were already server-rendered on page load - nothing to fetch.
        return;
      }

      const showMoreReviewsButton = document.querySelector('button.show-more-reviews');

      let pageNumber = 1;

      if (showMoreReviewsButton && showMoreReviewsButton.getAttribute('data-page')) {
        pageNumber = parseInt(showMoreReviewsButton.getAttribute('data-page'), 10);
      }

      await Product.loadReviews(pageNumber, true);
    }

    private static onGoToReviewListClick() {
      const reviewDetailsElement = element('details[data-action="toggle-review-details"]');
      if (!reviewDetailsElement.open) {
        reviewDetailsElement.open = true;
      }
    }

    static mountImageSlider() {
      const imageList = document.querySelector(
        '[data-container="product-images-slider"]'
      );

      if (imageList === null) {
        return;
      }

      setTimeout(() => {
        const slider = new Splide(imageList as HTMLElement, {
          type: 'loop',
          perPage: 1,
          speed: 500,
          dragMinThreshold: 20,
          flickMaxPages: 1,
          flickPower: 100,
          snap: false,
          arrows: true,
          breakpoints: {
            768: {
              pagination: false
            }
          },
          i18n: defaultTranslations
        }).mount();
      }, 0);
    }

    static mountProductPackageSlider() {
      const productPackageList = document.querySelector(
        '[data-container="product-package-slider"]'
      );

      if (productPackageList === null) {
        return;
      }

      const slideCount = productPackageList.querySelectorAll('.splide__slide').length;

      setTimeout(() => {
        new Splide(productPackageList as HTMLElement, {
          type: slideCount > 1 ? 'loop' : 'slide',
          perPage: 1,
          speed: 500,
          dragMinThreshold: 20,
          flickMaxPages: 1,
          flickPower: 100,
          snap: false,
          arrows: true,
          breakpoints: {
            1100: {
              pagination: false,
              padding: {
                left: 0,
                right: slideCount > 1 ? productPackageList.clientWidth * 0.10 : 0
              }
            },
            768: {
              pagination: false,
              padding: {
                left: 0,
                right: slideCount > 1 ? productPackageList.clientWidth * 0.22 : 0
              }
            }
          }
        }).mount();
      }, 0);
    }

    static setReferredListName() {
      const referredListName = localStorage.getItem("referred_list_name");
      const referredProductId = localStorage.getItem("referred_product_sku");
      const referredSponsorshipId = localStorage.getItem("referred_sponsorship_id");

      if (referredListName && referredProductId) {
        const product = document.querySelector(
          '[data-container="product-actions"]'
        )?.querySelector(
          `[data-product-sku="${referredProductId}"]`
        );

        if (product) {
          product.setAttribute("data-referred-list-name", referredListName);
        }

        if (referredSponsorshipId) {
          product.setAttribute("data-referred-sponsorship-id", referredSponsorshipId);
        }

        localStorage.removeItem("referred_list_name");
        localStorage.removeItem("referred_product_sku");
        localStorage.removeItem("referred_sponsorship_id");
      }
    }

    static getFassModal = (e) => {
      e.preventDefault();
      e.stopPropagation();
      const addToElement = element('body') as HTMLElement;
      const headerParam = (e.target as HTMLElement).attributes[
        'data-modal-header'
      ]?.value;

      // TODO: Check the intention of this code. I guess it should be able to be false.
      const isNplPackId = (e.target as HTMLElement).attributes['data-is-nplpackid']?.value === 'true' ?? false;
      const id = (e.target as HTMLElement).attributes['data-fass-id']?.value ?? false;
      const isAnimal = (e.target as HTMLElement).attributes['data-is-animal']?.value ?? false;
      if (addToElement) {
        const data = {
          header: headerParam || 'FASS',
          id,
          isNplPackId,
          isAnimal
        };
        Product.Instance.modal.AppendModal(
          ModalType.FassDocument,
          addToElement,
          data
        );
      }
    };

    static productVariantClick = (e) => {
      let targetElement: HTMLElement = e.target as HTMLElement;

      if (targetElement.nodeName !== 'A') {
        targetElement = targetElement.closest(
          '.relation-button'
        ) as HTMLElement;
      }

      const productId: Element = targetElement.attributes['data-product-id'];
      const productUrl: Element = targetElement.attributes['data-product-url'];
      const productPrice: Element = targetElement.attributes['data-product-price'];
      const productRelationValue: Element = targetElement.attributes['data-product-relation-value'];

      const event: IEasyfyEvent = {
        eventName: MedsEventNames.ProductVariantClick,
        data: {
          productId: productId.nodeValue,
          productUrl: productUrl.nodeValue,
          productPrice: productPrice.nodeValue,
          productRelationValue: productRelationValue.nodeValue
        }
      };

      Events.EasyfyEventHandler.RunEvent(event);
    };

    static showPriceInformation = async (e) => {
      const priceInformationDialog = document.querySelector('#earlier-price-info-dialog') as HTMLDialogElement;
      if (priceInformationDialog) {
        await DialogModule.ShowDialog(priceInformationDialog);
      }
    };

  }
}

export default Easyfy.Web.Pages;
